import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  id: string;
  icon: string;
  label: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

/**
 * Ligne de bloc d'accueil : activation + réordonnancement par glisser-déposer.
 */
export default function SortableBlockRow({ id, icon, label, description, enabled, onToggle }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        enabled ? 'bg-background-50 border-background-200/70' : 'bg-background-100/50 border-background-200/40'
      }`}
    >
      {/* Poignée */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Glisser pour réordonner"
        aria-label={`Réordonner ${label}`}
        className="w-7 h-7 flex items-center justify-center rounded text-foreground-300 hover:text-foreground-700 hover:bg-background-200/70 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
      >
        <i className="ri-drag-move-2-line"></i>
      </button>

      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-primary-50 text-primary-600' : 'bg-background-200/70 text-foreground-400'}`}>
        <i className={`${icon} text-lg`}></i>
      </div>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${enabled ? 'text-foreground-900' : 'text-foreground-500'}`}>{label}</p>
        <p className="text-xs text-foreground-400 line-clamp-1">{description}</p>
      </div>

      {/* Interrupteur */}
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onToggle(!enabled)}
        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
          enabled ? 'bg-primary-500' : 'bg-background-300/70'
        }`}
        title={enabled ? 'Masquer le bloc' : 'Afficher le bloc'}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
            enabled ? 'left-[22px]' : 'left-0.5'
          }`}
        ></span>
      </button>
    </div>
  );
}